/**
 * The image pipeline for the high graphics tier: ambient occlusion and bloom.
 *
 * Two things a forward renderer cannot do per object, and both are what
 * separates a rendered white room from a photographed one:
 *
 *   - AMBIENT OCCLUSION darkens the creases - where a chamber meets its lid,
 *     where a mainframe meets the floor, the corner between wall and
 *     ceiling. Without it every white surface is the same white and the
 *     tools float; with it they stand. Screen-space (GTAO), at half
 *     resolution, denoised, blended multiplicatively over the beauty pass.
 *   - BLOOM lets the things that are actually light sources - the luminaire
 *     panels, the wall strips, the status-tower lamps, the plasma - bleed
 *     into their surroundings the way a camera sensor does. Everything that
 *     should glow is already overdriven past 1.0 in linear light (see the
 *     material notes in builders.ts), so the threshold does the selecting:
 *     nothing merely lit ever blooms, only what emits.
 *
 * WHY ONLY THE HIGH TIER. The project promises integrated graphics, and the
 * lean tier is that promise: it renders exactly as it did before this file
 * existed - one direct pass to the screen, no intermediate targets. The high
 * tier is for machines the governor has measured and found comfortable, and
 * if this pipeline turns out to be the thing that breaks 45 fps, the same
 * governor demotes and locks, as it always has. So a slow machine pays for
 * none of this; a fast one gets the room it deserves.
 *
 * WHY NOT IN VR. Three's post-processing stack draws through its own
 * framebuffers, and a presenting XR session owns the framebuffer. The canvas
 * renders directly while a headset is on and comes back here when it is off.
 *
 * SHADERS ARE PAID FOR AT PREWARM. The passes' own programs, and the
 * render-to-target variants of every scene material, are compiled by one
 * throwaway render behind the splash - the same discipline as the lighting
 * prewarm, for the same reason: nothing may compile mid-walk. After that a
 * tier change is `enabled` flags flipping, which compiles nothing.
 */

import * as THREE from 'three'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js'
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js'
import { onHallChange } from './hall'
import { FAB } from './layout'
import { SUBFAB } from './subfabLayout'

/** Bloom: what survives the threshold, how far it spreads, how much it adds. */
const BLOOM = { threshold: 1.05, strength: 0.14, radius: 0.45 }

export class FabPostFx {
  private readonly composer: EffectComposer
  /** Public so the dev handle can tune them live; nothing else reads them. */
  readonly ao: GTAOPass
  readonly bloom: UnrealBloomPass
  private readonly smaa: SMAAPass
  private enabled = false

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.Camera,
    width: number,
    height: number,
  ) {
    const dpr = renderer.getPixelRatio()
    const w = Math.max(1, Math.floor(width * dpr))
    const h = Math.max(1, Math.floor(height * dpr))

    // Half float so the overdriven luminaires reach the bloom pass as the HDR
    // values they are.
    //
    // NO MULTISAMPLING, and this is the single most expensive decision in
    // the file. The direct path anti-aliases with the canvas's own MSAA, and
    // the first version of this target asked for the same - which on an
    // integrated GPU cost more than AO and bloom put together: a half-float
    // multisampled colour buffer plus its depth-stencil, resolved every
    // frame, halved the frame rate on its own (60 to 28 measured, before a
    // single effect was enabled). Edges are smoothed by the SMAA pass at the
    // end of the chain instead, which reads the finished picture once.
    const target = new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType })
    this.composer = new EffectComposer(renderer, target)
    this.composer.setPixelRatio(dpr)
    this.composer.setSize(width, height)

    this.composer.addPass(new RenderPass(scene, camera))

    // AO at half resolution: the effect is low-frequency by nature, and the
    // G-buffer pass it needs redraws the whole hall - halving both axes
    // makes that a quarter of the fill.
    this.ao = new GTAOPass(scene, camera, Math.ceil(w / 2), Math.ceil(h / 2))
    this.ao.output = GTAOPass.OUTPUT.Default
    this.ao.blendIntensity = 1.0
    this.ao.updateGtaoMaterial({
      // SEVENTY CENTIMETRES, and this is the number that decides whether the
      // hall looks rendered or photographed.
      //
      // It was 0.4, tuned in the sub-fab against overhead pipework, where a
      // tight radius is right: the features there are 100 mm cylinders seen
      // from two metres. The hall is the opposite problem. What has to read
      // is a two-metre cabinet meeting a floor, and that wants a soft pool
      // half a metre wide. At 0.4 the hall got a hairline at each joint,
      // which then went through a half-resolution buffer and a denoiser and
      // was gone: the AO buffer came back very nearly white, and raising
      // `blendIntensity` to 3.75x barely changed the image, because there
      // was no signal in it to amplify. Every tool in the building floated.
      //
      // The ceiling on this number is noise, not cost - measured, widening
      // the radius is free, because the expensive part is the G-buffer pass
      // that redraws the hall either way. At 0.95 and beyond, sixteen
      // samples at half resolution start to speckle across the big flat
      // wall panels, and the denoiser smears the speckle into blotches that
      // read as dirt. 0.7 is the widest that stays clean.
      radius: 0.7,
      distanceExponent: 1,
      // How deep a surface is assumed to extend behind what the camera sees.
      //
      // ONE METRE, AND THIS IS THE NUMBER THAT WAS SWITCHING THE PASS OFF.
      // It was 0.45, cut from the default to stop overhead pipes in the
      // sub-fab smearing a metre-thick shadow across the ceiling behind
      // them - and the note here already said the halo did not come back at
      // 1.0 either, so the cut was never needed. What it cost was measured
      // on the hall: at 0.45 the AO buffer darkened 0.9% of the frame past
      // 200/255 and bottomed at 134; at 1.0 it darkens 5% and bottoms at
      // 37. The test the shader runs is `abs(viewDelta.z) < thickness` per
      // sample, so at 0.45 any occluder whose depth differs from the shaded
      // point by more than 45 cm is thrown away - which, on a floor seen at
      // a grazing angle against two-metre cabinets, is most of them. The
      // radius note above describes a buffer that "came back very nearly
      // white" and blames the radius; this was the other half of it.
      thickness: 1.0,
      distanceFallOff: 1,
      scale: 1.3,
      samples: 16,
      screenSpaceRadius: false,
    })
    // A wider, three-ring denoise to match the wider radius: the same 16
    // samples spread over more screen area are noisier per pixel, and this
    // is what keeps the wall panels clean.
    this.ao.updatePdMaterial({ lumaPhi: 10, depthPhi: 2, normalPhi: 3, radius: 5, radiusExponent: 1, rings: 3, samples: 16 })
    // The hall and the sub-fab under it, and nothing beyond: the fog-white
    // void outside the walls contributes no occlusion worth sampling.
    //
    // Re-fitted on a hall switch: the ballroom is wider and shorter, and a
    // box fitted to the other hall would discard the AO across its outer
    // aisles (the shader discards outside the box).
    const clipToHall = (): void => {
      this.ao.setSceneClipBox(
        new THREE.Box3(
          new THREE.Vector3(-FAB.length / 2 - 1, -SUBFAB.depth - 1, -(FAB.bayDepth + FAB.aisleHalfWidth) - 1),
          new THREE.Vector3(FAB.length / 2 + 1, FAB.ceilingHeight + 1, FAB.bayDepth + FAB.aisleHalfWidth + 1),
        ),
      )
    }
    clipToHall()
    onHallChange(clipToHall)
    this.composer.addPass(this.ao)

    this.bloom = new UnrealBloomPass(
      new THREE.Vector2(w, h),
      BLOOM.strength,
      BLOOM.radius,
      BLOOM.threshold,
    )
    this.composer.addPass(this.bloom)

    // Tone mapping and the sRGB transfer, which the scene materials skip when
    // they draw into a render target - this pass is where the picture gets
    // the same ACES curve and exposure the direct path applies per material.
    this.composer.addPass(new OutputPass())

    // Anti-aliasing, last, on the display-ready image: SMAA finds the edges
    // in the picture itself, so it works on the tone-mapped result where
    // the edges the eye sees actually are, and costs a few fullscreen reads
    // rather than a multisampled scene.
    this.smaa = new SMAAPass(w, h)
    this.composer.addPass(this.smaa)
  }

  /** Follow the canvas. `width`/`height` in CSS pixels, as R3F reports them. */
  setSize(width: number, height: number): void {
    const dpr = this.renderer.getPixelRatio()
    this.composer.setPixelRatio(dpr)
    this.composer.setSize(width, height)
    const w = Math.max(1, Math.floor(width * dpr))
    const h = Math.max(1, Math.floor(height * dpr))
    this.ao.setSize(Math.ceil(w / 2), Math.ceil(h / 2))
    this.bloom.setSize(w, h)
    this.smaa.setSize(w, h)
  }

  /** Whether the next `render` goes through the pipeline or straight to screen. */
  setEnabled(on: boolean): void {
    this.enabled = on
  }

  get isEnabled(): boolean {
    return this.enabled
  }

  /**
   * Draw the frame. The direct path is byte-for-byte what the canvas drew
   * before this file existed, so the lean tier - and the headset - see no
   * change at all.
   */
  render(scene: THREE.Scene, camera: THREE.Camera, direct: boolean): void {
    if (direct || !this.enabled) {
      this.renderer.render(scene, camera)
      return
    }
    this.composer.render()
  }

  /**
   * Compile the scene's off-screen program variants, now.
   *
   * Two sets: the render-to-target variant of every scene material (a
   * different program from the to-screen one - tone mapping moves out of
   * the material when the target is not the screen), and the normal-material
   * variants the AO G-buffer pass draws the whole hall with. Both compiled
   * the way the lighting prewarm compiles - `renderer.compile`, which visits
   * EVERY object in the scene - rather than by rendering a frame, because a
   * frame compiles only what is in view from where the student happens to
   * be standing, and the rest would compile mid-walk when they turn around.
   *
   * Lit variants bake in the visible light count, so this is called from
   * inside the lighting prewarm's tier loop, once per tier - a pipeline
   * frame on the high tier must find its programs ready the moment the
   * governor promotes. `compile` ignores the scene's override material, so
   * the G-buffer set is reached by lending the normal material to every
   * mesh for the duration.
   */
  compileSceneVariants(): void {
    const { renderer, scene, camera } = this
    renderer.setRenderTarget(this.composer.renderTarget1)
    renderer.compile(scene, camera)

    const lent: Array<[THREE.Mesh, THREE.Material | THREE.Material[]]> = []
    scene.traverse((object) => {
      const mesh = object as THREE.Mesh
      if (mesh.isMesh && mesh.material) {
        lent.push([mesh, mesh.material])
        mesh.material = this.ao.normalMaterial
      }
    })
    // Any off-screen target compiles the same variant; the composer's is
    // still bound from above.
    renderer.compile(scene, camera)
    for (const [mesh, material] of lent) mesh.material = material
    renderer.setRenderTarget(null)
  }

  /**
   * Compile the passes' own shaders: one throwaway render with every pass
   * live, which is the only way to reach them. Behind the splash, after
   * `compileSceneVariants` has run for both tiers.
   */
  compilePasses(): void {
    const was = this.enabled
    this.enabled = true
    this.composer.render()
    this.enabled = was
  }
}
